import { FormControl, InputLabel, makeStyles } from '@material-ui/core'
import { FormControlProps } from '@material-ui/core/FormControl'
import { SelectProps } from '@material-ui/core/Select'
import { MenuItem, Select, styled } from 'opensolar-ui'
import { ChoicesProps, InputProps, useChoices, useInput, useTranslate } from 'ra-core'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { useFeatureFlag } from 'util/featureFlags'

import { EOE_KEYS } from './constants'

/** Hide EOE-related figures when the enable_eoe feature flag is off; keep selected EOE values visible. */
export function filterFeaturedFigureChoices<T extends { id: string }>(
  choices: T[],
  isEnableEoe: boolean | undefined,
  inputValue: string[] = []
): T[] {
  if (isEnableEoe) {
    return choices
  }
  const EOE_KEY_SET = new Set<string>(EOE_KEYS)
  const filtered = choices.filter((choice) => !EOE_KEY_SET.has(choice.id))
  const filteredIds = new Set(filtered.map((choice) => choice.id))
  for (const value of inputValue) {
    if (!value || !EOE_KEY_SET.has(value) || filteredIds.has(value)) {
      continue
    }
    const choice = choices.find((item) => item.id === value)
    if (choice) {
      filtered.push(choice)
      filteredIds.add(value)
    }
  }
  return filtered
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      margin: '16px 40px 16px 0px',
    },
  }),
  { name: 'FeatureFiguresSelector' }
)

const defaultValue = ['', '', '', '']

type FeatureFigure = {
  id: string
  name: string
}

const StyledSelect = styled(Select)({
  marginTop: '10px !important',
})

const FeatureFiguresSelector: FunctionComponent<ChoicesProps & InputProps<SelectProps> & FormControlProps> = (
  props
) => {
  const translate = useTranslate()
  const classes = useStyles()
  const {
    choices = [],
    classes: classesOverride,
    className,
    format,
    helperText,
    label,
    margin = 'dense',
    onBlur,
    onFocus,
    options,
    optionText,
    optionValue,
    parse,
    resource,
    emptyText = '',
    source,
    translateChoice,
    validate,
    variant = 'standard',
    ...rest
  } = props

  const { getChoiceText, getChoiceValue } = useChoices<FeatureFigure, string>({
    optionText,
    optionValue,
    translateChoice,
  })

  const isEnableEoe = useFeatureFlag('enable_eoe', 'on')

  const renderMenuItemOption = useCallback((choice) => getChoiceText(choice), [getChoiceText])

  const renderMenuItem = useCallback(
    (choice) => {
      return choice ? (
        <MenuItem key={getChoiceValue(choice)} value={getChoiceValue(choice)}>
          {renderMenuItemOption(choice)}
        </MenuItem>
      ) : null
    },
    [getChoiceValue, renderMenuItemOption]
  )

  const {
    input: { onChange, value: inputValue = defaultValue },
    meta: { error, touched },
  } = useInput({
    format,
    onBlur,
    onFocus,
    parse,
    resource,
    source,
    validate,
    ...rest,
  })

  const filteredChoices = useMemo(
    () => filterFeaturedFigureChoices(choices as FeatureFigure[], isEnableEoe, inputValue),
    [choices, isEnableEoe, inputValue]
  )

  const handleChange = useCallback(
    (name, index) => {
      const newValue = [...inputValue]
      newValue[index] = name
      onChange(newValue)
    },
    [inputValue]
  )
  return (
    <>
      {[1, 2, 3, 4].map((a, index) => {
        return (
          <FormControl margin={margin} key={a} classes={{ root: classes.root }}>
            <InputLabel shrink={true}>{`${translate('Featured Figures')} #${index + 1}`}</InputLabel>
            <StyledSelect
              autoWidth
              displayEmpty={true}
              error={!!(touched && error)}
              variant={variant}
              value={inputValue[index] || 'none'} // Using 'none' here to force it to always be in controlled value mode
              onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange(e.target.value, index)}
              {...options}
              MenuProps={{
                disableScrollLock: true,
                ...(options as { MenuProps?: Record<string, unknown> } | undefined)?.MenuProps,
              }}
            >
              <MenuItem
                value={''}
                key="null"
                aria-label={translate('ra.action.clear_input_value')}
                title={translate('ra.action.clear_input_value')}
              >
                {translate('None')}
              </MenuItem>
              {filteredChoices.map(renderMenuItem)}
            </StyledSelect>
          </FormControl>
        )
      })}
    </>
  )
}

export default FeatureFiguresSelector
